import React, { memo, useCallback, useContext, useMemo } from 'react'
import type { PLLHit } from '../types'
import {
  ResultRow,
  ResultThumbnail,
  ResultInfo,
  ResultName,
  ResultMeta,
  ResultChevron,
} from '../styles'
import { HighlightedText } from './HighlightedText'
import { AuthContext } from '../../../Api/firebase/authContext'
import { sendGamificationEngagement } from '../../../Api/gamification/gamification.api'
import { gamificationEngagements } from '../../../Constants'

type NewsResultItemProps = {
  item: PLLHit
  query: string
  onSelect: () => void
}

export const NewsResultItem = memo<NewsResultItemProps>(({ item, query, onSelect }) => {
  const { userId } = useContext(AuthContext)!

  const handleSelect = useCallback(async (): Promise<void> => {
    onSelect()
    if (userId && item?.objectID) {
      const engagement =
        item?.type === 'article'
          ? gamificationEngagements.ARTICLE_READ
          : item?.type === 'video'
          ? gamificationEngagements.VIDEO_WATCH
          : null
      if (engagement) {
        void sendGamificationEngagement(userId, engagement, item.objectID)
      }
    }
  }, [userId, item?.objectID, onSelect])

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent): Promise<void> => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleSelect()
      }
    },
    [handleSelect]
  )

  const typeLabel = useMemo(() => {
    if (item.type === 'video') return 'Video'
    if (item.type === 'article') return 'Article'
    return ''
  }, [item.type])

  return (
    <ResultRow
      role="option"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      aria-label={`${item.title}${typeLabel ? `, ${typeLabel}` : ''}`}
    >
      {item.image && (
        <ResultThumbnail src={item.image} alt="Result thumbnail" loading="lazy" />
      )}
      <ResultInfo>
        <ResultName>
          <HighlightedText text={item.title || ''} query={query} />
        </ResultName>
        {typeLabel && <ResultMeta>{typeLabel}</ResultMeta>}
      </ResultInfo>
      <ResultChevron aria-hidden="true">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </ResultChevron>
    </ResultRow>
  )
})

NewsResultItem.displayName = 'NewsResultItem'
